<template>
  <section class="bg-light px-4">
    <b-row no-gutters align-h="between" class="container-fluid">
      <b-col>
        <div class="d-inline-block mr-4">
          <h2 class="mb-4">{{ user.name }} {{ user.last_name }}</h2>
        </div>
        <div class="d-inline-block" v-if="isTeacher">
          <span v-if="group.pivot.status == 3">(suspendu)</span>
          <b-button
            class="ml-3 text-info"
            variant="link"
            @click="confirmation()"
            :disabled="btnLoading"
            >{{ btnSuspend }}
            <b-spinner class="ml-2" small v-if="btnLoading" />
          </b-button>
        </div>
      </b-col>
      <b-col class="text-md-right mb-3 mb-md-0">
        <div class="mr-lg-4 d-lg-inline-block">
          <span class="mr-2">Total de tes activités</span>
          <b-icon class="mr-2" icon="chat-fill" variant="primary"></b-icon>
          <span>{{ totalComments }}</span>
        </div>
        <div class="d-inline-block">
          Évaluation {{ totalAveragePoints | removeDecimals }} %
        </div>
      </b-col>
    </b-row>
    <b-row no-gutters align-h="between" class="container-fluid ">
      <b-col cols="12" md="3">
        <label class="text-muted mb-0" for="nickname">Nom d'utilisateur</label>
        <div>{{ user.nickname }}</div>
      </b-col>
      <b-col cols="12" md="3">
        <label class="text-muted mb-0" for="group">Groupe</label>
        <div>{{ group.name }}</div>
      </b-col>
      <b-col class="text-lg-right mt-3 mt-md-0">
        <div class="d-md-inline-block mr-md-3 mr-lg-5 text-success">
          Parce que ({{ comments.positive }})
        </div>
        <div class="d-md-inline-block mr-md-3 mr-lg-5 text-danger">
          Par contre ({{ comments.negative }})
        </div>
        <div class="d-md-inline-block mr-md-3 mr-lg-5 text-purple">
          Donc ({{ comments.neutral }})
        </div>
      </b-col>
    </b-row>
    <div>
      <b-modal
        ref="modal-suspend"
        title="Confirmation pour suspendre le participant"
        size="lg"
      >
        <b-container>
          <p class="my-4">
            Suspendre un participant ne lui permet plus d'accéder aux
            délibérations dédié à son groupe.
          </p>
          <p>
            Vous pouvez le réactiver si besoin après.
          </p>
          <p>
            Il pourra continuer à accéder à d'autre délibération soit d'un autre
            enseignant ou d'un autre groupe
          </p>
          <p>
            Si vous désirez supprimer son compte, veuillez communiquer avec
            votre administrateur
          </p>
        </b-container>
        <template v-slot:modal-footer="{ close }">
          <b-button class="text-info mr-4" variant="link" @click="close()">
            annuler
          </b-button>
          <b-button pill variant="primary" @click="onChange()">
            suspendre le participant
            <b-spinner class="ml-2" small v-if="btnLoading" />
          </b-button>
        </template>
      </b-modal>
    </div>
  </section>
</template>

<script>
/**
 * @file       Student summary component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import store from "@/store";
import { adminStudentMixin } from "@/modules/admin/components/students/mixins";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  name: "studentSummary",
  mixins: [adminStudentMixin],
  props: {
    groupId: Number
  },

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      btnLoading: false
    };
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    /**
     * Total average points.
     *
     * @return     {Object}  Total average points
     */
    totalAveragePoints: function() {
      return this.totalGrades.find(tg => tg.group == this.groupId)
        .totalAveragePoints;
    },

    /**
     * Group.
     *
     * @return     {Object}  Group
     */
    group: function() {
      return this.user.groups.find(u => u.id == this.groupId);
    },

    /**
     * Group comments.
     *
     * @return     {Object}  Group comments
     */
    comments: function() {
      return this.totalGrades.find(tg => tg.group == this.groupId)
        .group_comments_types;
    },

    /**
     * Total group comments.
     *
     * @return     {Object}  Total group comments
     */
    totalComments: function() {
      var comments = this.totalGrades.find(tg => tg.group == this.groupId)
        .group_comments_types;
      comments = comments.positive + comments.negative + comments.neutral;
      return comments ? comments : "--";
    },

    /**
     * Suspend button label according to the status.
     *
     * @return     {String}  The label
     */
    btnSuspend: function() {
      return this.group.pivot.status == 3
        ? "réactiver le participant"
        : "suspendre le compte";
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Confirmation action.
     */
    confirmation() {
      this.group.pivot.status == 3
        ? this.onChange()
        : this.$refs["modal-suspend"].show();
    },

    /**
     * Change event.
     */
    onChange() {
      this.btnLoading = true;
      const payload = {};
      if (this.group.pivot.status == 3) {
        payload.status = 1;
      } else {
        payload.status = 3;
      }
      payload.to_change_status = [
        {
          user: this.user.id,
          group: this.group.id
        }
      ];
      store
        .dispatch("groups/putStudentsArchived", payload)
        .then(() => {
          store.dispatch("admin/getUserByID", this.user.id).then(() => {
            this.btnLoading = false;
            this.$refs["modal-suspend"].hide();
            this.$root.$bvToast.toast(`Mise à jour effectuée avec succès.`, {
              title: "Succès"
            });
          });
        })
        .catch(() => {
          this.btnLoading = false;
          this.$refs["modal-suspend"].hide();
          this.$root.$bvToast.toast(`La mise à jour à échouée.`, {
            title: "Erreur",
            variant: "danger"
          });
        });
    }
  }
};
</script>
<style lang="scss" scoped></style>
